import { useRef } from 'react';
import styled, { css } from 'styled-components';
import { useCameraFrames, useSignedUrls } from './useCameraFrames';
import { useFrameBrowser } from './useFrameBrowser';
import { useTimelapse } from './useTimelapse';
import { useMediaMode } from '../../hooks/useMediaMode';
import PanelHeader from '../Hud/PanelHeader';
import ModeSwitch from '../Hud/ModeSwitch';
import FrameNav from '../Hud/FrameNav';
import TimelapseBar, { TimelapseToggle } from '../Hud/TimelapseBar';
import TelemetryStack from '../Hud/TelemetryStack';
import TrendChart from '../Hud/TrendChart';
import { COLORS } from '../../lib/theme';
import { formatAge, formatAgeMinSec, ageColor } from '../../lib/units';

const PORTRAIT_ID = 0;
const LANDSCAPE_ID = 1;

// Below this width the side-by-side split leaves both frames unreadable, so
// the grid collapses to a single scrolling column.
const MOBILE = '900px';

const Root = styled.div`
  display: grid;
  grid-template-columns: minmax(230px, 0.42fr) minmax(0, 1fr);
  grid-template-rows: minmax(0, 1fr) minmax(120px, 0.32fr);
  gap: 2px;
  width: 100%;
  height: 100%;
  min-height: 480px;
  background: ${COLORS.ground};

  @media (max-width: ${MOBILE}) {
    grid-template-columns: minmax(0, 1fr);
    grid-template-rows: none;
    grid-auto-rows: min-content;
    height: auto;
    min-height: 100%;
  }
`;

const Panel = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
  min-height: 0;
  background: ${COLORS.feedGround};
  border: 1px solid ${COLORS.hairline};
`;

const Cam0Panel = styled(Panel)`
  grid-column: 1;
  grid-row: 1 / span 2;

  @media (max-width: ${MOBILE}) {
    grid-column: 1;
    grid-row: auto;
  }
`;

const Cam1Panel = styled(Panel)`
  grid-column: 2;
  grid-row: 1;

  @media (max-width: ${MOBILE}) {
    grid-column: 1;
    grid-row: auto;
  }
`;

const ChartRow = styled.div`
  grid-column: 2;
  grid-row: 2;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 2px;
  min-width: 0;
  min-height: 0;

  @media (max-width: ${MOBILE}) {
    grid-column: 1;
    grid-row: auto;
    grid-template-columns: 1fr;
    grid-auto-rows: 150px;
  }
`;

// The stream fills the box; its intrinsic aspect ratio must never size the
// grid row, or the overlays get pushed off screen.
const Video = styled.div`
  position: relative;
  flex: 1 1 auto;
  min-height: 0;
  overflow: hidden;

  @media (max-width: ${MOBILE}) {
    flex: 0 0 auto;
    aspect-ratio: ${(p) => p.$mobileAspect};
  }

  /* CAM 1's telemetry stack is taller than a 16:9 frame is on a phone, so
     that pane grows to its content instead of clipping the stack. */
  ${(p) =>
    p.$mobileGrow &&
    css`
      @media (max-width: ${MOBILE}) {
        aspect-ratio: auto;
        height: auto;
        min-height: 56vw;
      }
    `}
`;

const Media = styled.img`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const VideoMedia = styled.video`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const Empty = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: 'IBM Plex Mono', ui-monospace, monospace;
  font-weight: 600;
  font-size: 9px;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: ${COLORS.label};
`;

// A clip plays once and then holds its last frame: looping a 10s recording
// reads as live, and the header's frame age is what says how old it is.
// Native controls stay on so it can be replayed or scrubbed -- the overlay
// plates above it are pointer-events: none, so clicks reach them.
//
// A frame whose URL is still being signed keeps the previous one of the same
// kind on screen rather than blanking: stepping through history would
// otherwise flash empty on every press.
function Stream({ cameraId, frame, empty }) {
  const lastShownRef = useRef(null);
  if (frame?.url) lastShownRef.current = frame;

  const held = lastShownRef.current;
  const shown = frame?.url ? frame : frame && held?.isVideo === frame.isVideo ? held : null;

  if (!shown) return <Empty>{frame ? 'Loading' : empty}</Empty>;

  return shown.isVideo ? (
    <VideoMedia key={shown.url} src={shown.url} autoPlay muted playsInline controls />
  ) : (
    <Media src={shown.url} alt={`Camera ${cameraId} frame`} />
  );
}

function stampFor(frame) {
  if (!frame?.takenAt) return '-- UTC';
  return frame.takenAt.toISOString().replace('T', ' ').slice(0, 19) + ' UTC';
}

/**
 * Four panels in a 2px-gutter grid: CAM 0 down the left spanning both rows,
 * CAM 1 top-right, and the two chart panels beneath it. `children` is the
 * CAM 0 overlay layer; `landscapeOverlay` goes over CAM 1 next to the
 * telemetry stack.
 *
 * Each header carries prev/next arrows through the stored frames and the
 * stamp and age of whichever frame is showing -- the per-feed equivalent of
 * the session timer in the reference design. The two panels step together:
 * an arrow on either camera picks one of its frames, and the other panel
 * shows the frame it has closest to that moment. CAM 1's header also holds
 * the PHOTO/VIDEO switch and the LAPSE toggle, which apply to both panels.
 *
 * Only the frames on screen and their neighbours are signed, so a press on
 * either arrow is usually instant and history is never signed up front.
 *
 * In photo mode LAPSE runs one timelapse across both panels, side by side
 * over a merged timeline; while it runs, either camera's arrows step it and
 * the stamps follow the frames being played.
 */
export default function CameraFeed({
  mqttClient,
  cameraHostname,
  fields,
  linkState,
  rates,
  history,
  hourly,
  nowMs,
  children,
  landscapeOverlay,
}) {
  const { cameras } = useCameraFrames(true, mqttClient, cameraHostname);
  const [mode, setMode] = useMediaMode();
  const byId = Object.fromEntries(cameras.map((c) => [c.cameraId, c]));
  const views = useFrameBrowser(cameras, mode);
  const lapse = useTimelapse(cameras, nowMs, mode === 'photo');
  const view0 = views[PORTRAIT_ID];
  const view1 = views[LANDSCAPE_ID];

  const neighbours = (id, view) => {
    const frames = byId[id]?.[mode] ?? [];
    return view
      ? [frames[view.index - 1], frames[view.index], frames[view.index + 1]]
          .filter(Boolean)
          .map((f) => f.filename)
      : [];
  };
  const urls = useSignedUrls([...neighbours(PORTRAIT_ID, view0), ...neighbours(LANDSCAPE_ID, view1)]);
  const withUrl = (frame) => (frame ? { ...frame, url: urls[frame.filename] || null } : null);

  const cam0 = lapse.active ? lapse.frames[PORTRAIT_ID] ?? null : withUrl(view0?.frame);
  const cam1 = lapse.active ? lapse.frames[LANDSCAPE_ID] ?? null : withUrl(view1?.frame);
  const emptyText = lapse.active ? 'No frames in range' : mode === 'video' ? 'No video' : 'No frame';

  const ageOf = (frame) => (frame?.takenAt ? nowMs - frame.takenAt.getTime() : null);
  // Minutes and seconds while the frame is recent, so a live feed keeps the
  // shape it had; a frame stepped back to from days ago reads as "2d 4h",
  // not as thousands of minutes.
  const frameAge = (frame) => {
    const age = ageOf(frame);
    if (age == null) return null;
    return age >= 3_600_000 ? formatAge(age) : formatAgeMinSec(age);
  };
  // The arrows drive the shared timelapse cursor while one is running, and
  // the stored-frame browser otherwise.
  const navFor = (view) =>
    lapse.active ? (
      <FrameNav
        index={lapse.index}
        count={lapse.count}
        atOldest={lapse.index <= 0}
        atNewest={lapse.index >= lapse.count - 1}
        live={false}
        onOlder={() => lapse.step(-1)}
        onNewer={() => lapse.step(1)}
      />
    ) : (
      <FrameNav
        index={view?.index ?? -1}
        count={view?.count ?? 0}
        atOldest={view?.atOldest ?? true}
        atNewest={view?.atNewest ?? true}
        live={view?.isLive ?? true}
        onOlder={view?.older}
        onNewer={view?.newer}
      />
    );

  return (
    <Root>
      <Cam0Panel>
        <PanelHeader
          camId="CAM 0"
          stamp={stampFor(cam0)}
          timer={frameAge(cam0)}
          timerColor={ageColor(ageOf(cam0))}
          controls={navFor(view0)}
        />
        <Video $mobileAspect="9 / 16">
          <Stream cameraId={PORTRAIT_ID} frame={cam0} empty={emptyText} />
          {children}
        </Video>
      </Cam0Panel>

      <Cam1Panel>
        <PanelHeader
          camId="CAM 1"
          stamp={stampFor(cam1)}
          timer={frameAge(cam1)}
          timerColor={ageColor(ageOf(cam1))}
          controls={
            <>
              {navFor(view1)}
              {/* Stills only, like weco-dashboard's gallery: no LAPSE in video mode. */}
              {mode === 'photo' && <TimelapseToggle on={lapse.on} onToggle={lapse.toggle} />}
              <ModeSwitch mode={mode} onChange={setMode} />
            </>
          }
        />
        {lapse.active && <TimelapseBar timelapse={lapse} />}
        <Video $mobileAspect="16 / 9" $mobileGrow>
          <Stream cameraId={LANDSCAPE_ID} frame={cam1} empty={emptyText} />
          {landscapeOverlay}
          <TelemetryStack fields={fields} linkState={linkState} rates={rates} />
        </Video>
      </Cam1Panel>

      <ChartRow>
        {/* Position is a continuous quantity: the spike envelope reads as a
            sawtooth on it and hides the wave-period swing that is the point
            of the plot. Power keeps the spikes -- its peaks are real, and the
            footer already carries its averages. */}
        <TrendChart
          label="Rack position"
          points={history?.rackPosition}
          unit="m"
          format={(v) => v.toFixed(2)}
          nowMs={nowMs}
          traceColor={COLORS.live}
          smooth
        />
        <TrendChart
          label="PWR · INA"
          points={history?.inaPower}
          unit="W"
          format={(v) => v.toFixed(1)}
          nowMs={nowMs}
          traceColor={COLORS.label}
          showAverage
          hourly={hourly?.inaPower}
        />
      </ChartRow>
    </Root>
  );
}
